import React, { useState, useEffect, useMemo, useRef } from 'react';
import DOMPurify from 'dompurify';
import { BookingSplitRow, Report, AppSettings, UserProfile, ReportLanguage, ServiceTransaction } from '../types';
import { generatePDF } from '../services/pdfService';
import { Button } from './Button';
import { holdIdleExit } from '../services/idleHold';
import { Mail, X, ExternalLink, CheckCircle2, ArrowRight, Layers, CloudLightning, Loader2, PenTool, AlertTriangle, RefreshCw } from 'lucide-react';
import { saveReportSnapshot, sendReportEmail, updateProjectExportStatus, logEmailAttempt, saveProjectToCloud, uploadPdf } from '../services/cloudService';
import { applyReportBookingWithNotices, BookingSkipNotice, flatTargetForInspectionChoice, isReportBooked, redirectMainBookingsToFlat } from '../services/reportBooking';
import { flushJournalMirror, getServiceLog, queueJournalMirror } from '../services/serviceJournal';
import { planDueDateAfterSend } from '../services/dateService';
import { addPendingSendMarker, readPendingSendMarkers, updatePendingSendMarker, removePendingSendMarker, newSendKey } from '../services/pendingSendPersistence';
import { hasOpenDefects, getDefaultEmailBody } from '../services/emailTemplates';
import { buildActorName } from '../services/actorName';
import { bereiteFreimeldungLinkVor } from '../services/freimeldungService';
import { selectBillableServices } from '../services/billingSelection';
import { bookingIsFlatProof } from '../services/flatRateLogic';
import { flatRedirectHinweis } from '../services/reportRebookLogic';
import { isNepService } from '../services/nepPosition';
import { istUebungsprojekt } from '../services/uebung/uebungService';
import { UEBUNG_BLOCK } from '../services/sendGuard';
import { istNativeApp } from '../services/nativePlatform';
import { ausgabeFehlerMelden, dateiAusgeben, dateiInCacheSchreiben, dateiTeilen, mailEntwurfMitDatei, MailUebergabe } from '../services/dateiAusgabe';
import { externOeffnen } from '../services/externOeffnen';
import { isBuildGuardWriteError, istBuildVeraltet, istVorVersandVeraltet } from '../services/versionGate';

/**
 * Die Schritte eines Versands in der Reihenfolge, in der sie ablaufen.
 * Die Reihenfolge ist nicht kosmetisch: 'send' vor 'save'/'archive' bildet ab,
 * dass die E-Mail zuerst draußen ist und Buchung wie Archiv erst danach
 * entstehen. Genau daran liest der Nutzer ab, was schon passiert ist.
 */
type SendStepKey = 'pdf' | 'upload' | 'send' | 'log' | 'save' | 'archive';

const SEND_STEP_LABELS: Record<SendStepKey, { title: string; hint: string }> = {
  pdf:     { title: 'Bericht wird erzeugt',         hint: 'PDF mit Fotos und Anlagen zusammenstellen' },
  upload:  { title: 'PDF wird hochgeladen',         hint: 'Ablage in der Cloud als Mailanhang' },
  send:    { title: 'E-Mail wird versendet',        hint: 'Übergabe an den Mailserver' },
  log:     { title: 'Sendebericht wird gesichert',  hint: 'Eintrag im Sendeverlauf' },
  save:    { title: 'Leistung wird gebucht',        hint: 'Abrechnung und Projektstand speichern' },
  archive: { title: 'Bericht wird archiviert',      hint: 'Unveränderliche Zweitschrift anlegen' },
};

/** Lesbarer Text eines Fehlers (Error, Supabase-Fehlerobjekt oder sonst etwas). */
const fehlerText = (fehler: unknown): string => {
  if (fehler instanceof Error) return fehler.message;
  if (fehler && typeof fehler === 'object' && 'message' in fehler) return String((fehler as { message: unknown }).message ?? '');
  return fehler == null ? '' : String(fehler);
};

/** `name` des Fehlers, wenn der Nutzer das Warten auf einen hängenden Schritt beendet. */
const WARTEN_BEENDET = 'WartenBeendet';

const wartenBeendetFehler = (): Error => {
  const fehler = new Error('Warten beendet – keine Antwort.');
  fehler.name = WARTEN_BEENDET;
  return fehler;
};

const fehlerName = (fehler: unknown): string =>
  fehler && typeof fehler === 'object' && 'name' in fehler ? String((fehler as { name: unknown }).name) : '';

/**
 * Wartet auf `arbeit` — oder nur, bis der Nutzer „Warten beenden" tippt
 * (`ausweg.current()`); dann wirft es einen Fehler namens WARTEN_BEENDET.
 * Die Arbeit selbst läuft weiter (Upload und fetch haben hier keinen Abbruch),
 * ihr Ergebnis wird nur nicht mehr abgewartet.
 */
const mitAusweg = <T,>(arbeit: Promise<T>, ausweg: { current: (() => void) | null }): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const beenden = () => reject(wartenBeendetFehler());
    ausweg.current = beenden;
    const fertig = () => { if (ausweg.current === beenden) ausweg.current = null; };
    arbeit.then(
      (wert) => { fertig(); resolve(wert); },
      (fehler: unknown) => { fertig(); reject(fehler); },
    );
  });

/**
 * Kam gar keine Antwort (kein Netz, Abbruch)? `fetch` meldet das je Browser
 * anders („Failed to fetch", „Load failed", „NetworkError …"); Supabase reicht
 * den Text durch. Dazu der eigene NetzFehler der Speicherwege und der
 * Netzfehler von supabase-auth. Ohne Netz sagt es `navigator.onLine` direkt.
 * NICHT „fetch failed": so meldet Node auf dem SERVER ein gescheitertes Laden
 * des PDFs — dann hat der Server geantwortet, und die Mail ging sicher nicht hinaus.
 */
const istVerbindungsfehler = (fehler: unknown): boolean => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  const name = fehlerName(fehler);
  if (name === 'NetzFehler' || name === 'AuthRetryableFetchError') return true;
  return /failed to fetch|load failed|networkerror|network request failed/i.test(fehlerText(fehler));
};

/**
 * Anzeige nach einem Fehlschlag VOR dem Senden (PDF, Upload, Vorbereitung):
 * sendReportEmail lief nie — es ist nichts hinausgegangen.
 */
const nichtVersendetMeldung = (schritt: 'pdf' | 'upload' | 'vorbereitung', fehler: unknown): { text: string; detail: string | null; hinweis: string } => {
  if (fehlerName(fehler) === WARTEN_BEENDET) {
    return { text: 'Nicht versendet – Warten beendet. Es ist nichts hinausgegangen.', detail: null, hinweis: 'Erneut senden ist ohne Bestätigung möglich, am besten mit besserem Netz.' };
  }
  if (istVerbindungsfehler(fehler)) {
    return { text: 'Nicht versendet – keine Verbindung. Es ist nichts hinausgegangen.', detail: null, hinweis: 'Sobald wieder Netz da ist, einfach erneut senden.' };
  }
  const was = schritt === 'pdf' ? 'das PDF ließ sich nicht erstellen' : schritt === 'upload' ? 'das PDF ließ sich nicht hochladen' : 'Fehler bei der Vorbereitung';
  return { text: `Nicht versendet – ${was}. Es ist nichts hinausgegangen.`, detail: fehlerText(fehler) || null, hinweis: 'Erneut senden ist ohne Bestätigung möglich.' };
};

/**
 * Der Tab ist veraltet (neue Programmversion ausgeliefert): er darf nicht
 * speichern, also auch nicht senden. Vorfall 6051 #149 (28.09.2026): die
 * Mail ging hinaus, Buchung und Archiv wurden verweigert.
 */
const VERALTET_NICHT_VERSENDET: { text: string; detail: string | null; hinweis: string } = {
  text: 'Nicht versendet – es gibt eine neue Programmversion. Es ist nichts hinausgegangen.',
  detail: 'Aus diesem Fenster würde der Bericht zwar verschickt, aber weder abgerechnet noch archiviert.',
  hinweis: 'Bitte diesen Dialog schließen, oben im Balken „Jetzt aktualisieren“ tippen und danach erneut senden. Ihre Eingaben sind lokal gesichert.',
};

/** Text nach einem Fehler IM Senden: der des Servers; ohne Antwort ein deutscher Ersatz. */
const serverFehlerText = (fehler: unknown): string => {
  // Ältere WebViews melden den Abbruch als AbortError statt mit dem übergebenen Grund.
  if (fehlerName(fehler) === WARTEN_BEENDET || fehlerName(fehler) === 'AbortError') return 'Keine Antwort vom Mailserver – Warten beendet.';
  if (istVerbindungsfehler(fehler)) return 'Keine Antwort vom Mailserver – die Verbindung ist abgebrochen.';
  return fehlerText(fehler) || 'Fehler beim Senden der E-Mail.';
};

/** Nach einem Fehlschlag im Server-Weg geht das PDF zur Sicherheit ans Gerät — der Kasten sagt, warum. */
const PDF_RUECKFALL_TEXT: Record<'geteilt' | 'geladen', string> = {
  geteilt: 'Das PDF wurde zum Speichern/Teilen übergeben. Entweder von Hand verschicken oder hier erneut senden – nicht beides.',
  geladen: 'Das PDF wurde heruntergeladen. Entweder von Hand verschicken oder hier erneut senden – nicht beides.',
};

/** NUR App: Titel und Text im Schritt 'instruction' je nach Übergabeweg (Vertrag E19). */
const APP_MAIL_TEXTE: Record<MailUebergabe | 'laeuft' | 'ohne_pdf', { titel: string; text: string }> = {
  laeuft:      { titel: 'E-Mail wird vorbereitet',  text: 'Das PDF wird an die Mail-App übergeben …' },
  entwurf:     { titel: 'E-Mail-Entwurf geöffnet',  text: 'Das PDF wurde mit Empfängern, Betreff und Text an die Mail-App übergeben. Bitte dort prüfen und absenden. Falls kein Entwurf erschienen ist: Berichtsvorschau öffnen → PDF teilen.' },
  abgebrochen: { titel: 'Übergabe abgebrochen',     text: 'Die Auswahl der Mail-App wurde geschlossen. Das PDF lässt sich über die Berichtsvorschau erneut ausgeben.' },
  rueckfall:   { titel: 'E-Mail Programm geöffnet', text: 'Das PDF wurde über den Teilen-Dialog ausgegeben. Bitte fügen Sie es an die E-Mail an.' },
  ohne_pdf:    { titel: 'E-Mail Programm geöffnet', text: 'Das PDF konnte nicht übergeben werden. Bitte über die Berichtsvorschau erneut ausgeben und an die E-Mail anfügen.' },
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  report: Report;
  settings?: AppSettings;
  onUpdate?: (report: Report) => void;
  activeSectionId?: string | null;
  projectId?: string | null;
  onPoolarTrigger?: () => void; 
  onFinished?: () => void; 
  precapturedHours?: number; 
  additionalAddons?: Record<string, number>; 
  precapturedServiceId?: string | null;
  /**
   * Aufteilung der Begehung auf mehrere LV-Positionen (Lose). Fehlt sie, gilt
   * `precapturedServiceId` — so laufen Aufrufer ohne Aufteilung und alle vor
   * dieser Aenderung gespeicherten Berichte unveraendert weiter.
   */
  precapturedBookings?: BookingSplitRow[];
  userProfile?: UserProfile | null; // NEU
  /**
   * Bewusster Wiederholungsversand nach dem Schon-versendet-Check: nur der
   * Sendebericht (email_logs) bekommt das [Wiederholung]-Präfix, damit die
   * zweite Mail im Sendeverlauf nicht wie ein eigenständiger Bericht aussieht.
   * Der Betreff der Mail an den Kunden bleibt davon unberührt.
   */
  isRepeatSend?: boolean;
}

export const EmailModal: React.FC<Props> = ({ isOpen, onClose, report, settings, onUpdate, activeSectionId, projectId, onFinished, precapturedHours = 1, additionalAddons = {}, precapturedServiceId, precapturedBookings, userProfile, isRepeatSend = false }) => {
  const [step, setStep] = useState<'config' | 'instruction'>('config');
  // NUR App, lokaler/Outlook-Weg: wie PDF und Mail an die Mail-App gingen —
  // steuert den Text im Schritt 'instruction'. Im Browser immer null.
  const [appMailUebergabe, setAppMailUebergabe] = useState<MailUebergabe | 'laeuft' | 'ohne_pdf' | null>(null);

  /**
   * ÜBUNGSPROJEKT — hier geht nichts hinaus (Plan 8.2).
   *
   * Erkannt am Bericht selbst, weil dieses Fenster die Projektzeile nie in der
   * Hand hat; `istUebungsprojekt` liest dafür `metadata.globalData.istUebung`
   * und die Projektnummer. Der Senden-Knopf wird ERSETZT statt nur gesperrt:
   * ein ausgegrauter Knopf ohne Begründung sieht aus wie ein Fehler, und der
   * nächste Handgriff wäre „nochmal versuchen" (dieselbe Überlegung wie beim
   * Schreibschutz, services/readOnlyNotice.ts).
   *
   * Die Nummer muss AUSDRÜCKLICH umgehängt werden: `istUebungsprojekt` sucht
   * sie unter `project_number` (so heisst die Spalte), ein `Report` führt sie
   * aber als `metadata.projectNumber`. Wer nur `report` durchreicht, verliert
   * genau den Weg, der ohne sauberen Blob noch greift.
   */
  const istUebung = istUebungsprojekt({ metadata: report.metadata, project_number: report.metadata?.projectNumber });

  /**
   * Solange dieses Fenster offen ist, darf sich das Projekt nicht von selbst
   * schliessen.
   *
   * Der Leerlauf-Rueckzug (30 Minuten ohne Eingabe, App.tsx) haengt die
   * Projektansicht aus — mitsamt dem hier getippten Betreff und Text, dem
   * Stand des Assistenten und einem eventuell laufenden Versand. Wer nach
   * einer langen Unterbrechung an den Platz zurueckkehrt, soll seine Mail
   * vorfinden und nicht die Projektliste.
   */
  useEffect(() => {
    if (!isOpen) return;
    return holdIdleExit('email-modal');
  }, [isOpen]);
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  
  const [sendMethod, setSendMethod] = useState<'client' | 'server'>('server');
  const [serverSendStatus, setServerSendStatus] = useState<'idle' | 'success' | 'error' | 'persisting' | 'persist_error'>('idle');
  // Spiegel als Ref: handleProcess läuft über mehrere await-Punkte, sein
  // finally-Block sieht sonst immer den Stand beim Aufruf ('idle') und gäbe den
  // Senden-Button auch nach erfolgreichem Versand wieder frei.
  const serverSendStatusRef = useRef<'idle' | 'success' | 'error' | 'persisting' | 'persist_error'>('idle');
  const updateSendStatus = (next: 'idle' | 'success' | 'error' | 'persisting' | 'persist_error') => {
      serverSendStatusRef.current = next;
      setServerSendStatus(next);
  };
  // In-Flight-Lock als Ref: isGenerating wirkt erst nach dem Re-Render, ein
  // schneller Doppelklick startet sonst zwei Versandläufe.
  const isProcessingRef = useRef(false);
  const [pendingMarkerId, setPendingMarkerId] = useState<string | null>(null);
  // Nach einem Fehlversuch: Zeitpunkt merken und den Button erst nach
  // ausdrücklicher Bestätigung wieder freigeben — die Mail kann trotz Fehler
  // zugestellt worden sein (Timeout nach dem Absetzen).
  const [lastFailedAttemptAt, setLastFailedAttemptAt] = useState<string | null>(null);
  const [retryConfirmed, setRetryConfirmed] = useState(false);
  // Text des Servers zum letzten Fehlversuch IM Senden (roter Kasten).
  const [sendeFehlerText, setSendeFehlerText] = useState<string | null>(null);
  // Fehlschlag VOR dem Senden: nichts ist hinausgegangen, der Status bleibt
  // 'idle' — der nächste Versuch braucht deshalb keine Bestätigung.
  const [nichtVersendet, setNichtVersendet] = useState<{ text: string; detail: string | null; hinweis: string } | null>(null);
  // Speichern nach dem Versand scheiterte, weil der Tab veraltet ist: ein
  // erneuter Versuch aus diesem Tab scheitert genauso — erst Neuladen hilft
  // (danach holt der Nachhol-Marker Abrechnung und Archiv nach).
  const [persistVeraltet, setPersistVeraltet] = useState(false);
  const [bookingNotices, setBookingNotices] = useState<BookingSkipNotice[]>([]);
  // Die Buchung hat eine Begehungsposition auf die Pauschale umgelenkt
  // (reportBooking, `redirectedToFlat`) — das sagt der Abschluss, sonst sucht
  // der Anwender die Begehung auf der Position, die er gewaehlt hatte.
  const [flatRedirectNotice, setFlatRedirectNotice] = useState<string | null>(null);
  const [emailLanguage, setEmailLanguage] = useState<ReportLanguage>(report.metadata.reportLanguage || 'de');
  
  const [forceBilling, setForceBilling] = useState(false);
  const [manualServiceId, setManualServiceId] = useState('');

  const deliveryConfig = report.metadata.globalData?.deliveryConfig;

  // Der Versand besteht aus mehreren Schritten, die unterschiedlich lange
  // dauern und unterschiedlich schwer wiegen. Vorher stand über die ganze
  // Strecke nur ein Spinner — wer bei "Daten werden gespeichert" abbrach,
  // konnte nicht wissen, ob die E-Mail schon raus war.
  const [currentStep, setCurrentStep] = useState<SendStepKey | null>(null);

  // Hängt ein Schritt (Funkloch: Verbindung steht, aber nichts kommt durch),
  // bietet der Dialog nach einer Weile „Warten beenden" an — während des Laufs
  // gibt es sonst keinen Ausweg (kein X, kein Abbrechen). Das Senden bekommt
  // mehr Zeit: danach bleibt offen, ob die Mail hinausging.
  const wartenBeendenRef = useRef<(() => void) | null>(null);
  const [wartetLange, setWartetLange] = useState<SendStepKey | null>(null);
  // Steht der Dialog noch (offen und eingehängt)? Ein im Hintergrund zu Ende
  // gelaufener Versand öffnet danach kein Rückfall-PDF mehr aus dem Nichts.
  const sichtbarRef = useRef(false);
  useEffect(() => {
      sichtbarRef.current = isOpen;
      return () => { sichtbarRef.current = false; };
  }, [isOpen]);
  useEffect(() => {
      if (currentStep !== 'pdf' && currentStep !== 'upload' && currentStep !== 'send') return;
      const schritt = currentStep;
      const uhr = window.setTimeout(() => setWartetLange(schritt), schritt === 'send' ? 90_000 : 60_000);
      return () => window.clearTimeout(uhr);
  }, [currentStep]);
  const wartenBeendbar = wartetLange !== null && wartetLange === currentStep;
  // Wohin das Rückfall-PDF nach einem Fehlschlag im Server-Weg ging.
  const [pdfRueckfall, setPdfRueckfall] = useState<'geteilt' | 'geladen' | null>(null);

  /**
   * Welche Schritte dieser Versand durchläuft. Der lokale Weg und der
   * Poolarserver erzeugen weder Upload noch Mailversand — dort dürfen die
   * Schritte auch nicht in der Liste stehen, sonst zeigt die Anzeige einen
   * Fortschritt an, der nie erreicht wird.
   */
  const sendSteps = useMemo<SendStepKey[]>(() => {
      const serverSend = sendMethod === 'server' && deliveryConfig?.method !== 'poolarserver';
      const steps: SendStepKey[] = ['pdf'];
      if (serverSend) steps.push('upload', 'send', 'log');
      steps.push('save');
      if (projectId) steps.push('archive');
      return steps;
  }, [sendMethod, deliveryConfig?.method, projectId]);

  const stepIndex = currentStep ? sendSteps.indexOf(currentStep) : -1;
  const stepPercent = stepIndex < 0 ? 0 : Math.round(((stepIndex + 1) / sendSteps.length) * 100);
  // Ab hier ist die E-Mail beim Mailserver. Das muss auf dem Schirm stehen:
  // wer jetzt abbricht, hat einen versendeten Bericht ohne Buchung — der
  // Marker holt das zwar nach, aber niemand soll darüber im Unklaren sein.
  const mailAlreadySent = sendSteps.includes('send') && stepIndex > sendSteps.indexOf('send');

  /**
   * Die Schrittliste. `activeIndex === null` heißt: alles erledigt — dann
   * trägt jede Zeile ihren grünen Haken.
   *
   * Bewusst dieselbe Liste im Verlauf UND auf dem Abschlussbildschirm: Wer
   * gerade zugesehen hat, wie die Haken gesetzt wurden, soll das Ergebnis am
   * Ende noch vor sich haben und nicht auf einen Satz vertrauen müssen.
   */
  const renderStepList = (activeIndex: number | null) => (
      <ol className="space-y-2.5">
          {sendSteps.map((key, i) => {
              const done = activeIndex === null || i < activeIndex;
              const active = activeIndex !== null && i === activeIndex;
              return (
                  <li key={key} className="flex items-center gap-3">
                      <span className="w-5 h-5 shrink-0 flex items-center justify-center">
                          {done && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                          {active && <Loader2 className="w-4 h-4 text-indigo-600 animate-spin" />}
                          {!done && !active && <span className="w-2 h-2 rounded-full bg-slate-300" />}
                      </span>
                      <span className={`text-sm ${active ? 'font-bold text-slate-800' : done ? 'text-slate-500' : 'text-slate-400'}`}>
                          {SEND_STEP_LABELS[key].title}
                      </span>
                  </li>
              );
          })}
      </ol>
  );

  // Update language if report meta changes (initially)
  useEffect(() => {
    setEmailLanguage(report.metadata.reportLanguage || 'de');
  }, [report.metadata.reportLanguage]);

  // Update templates when language changes
  useEffect(() => {
    if (isOpen) {
        const meta = report.metadata;
        const replacements: Record<string, string> = {
            '{projekt}': meta.projectName || '',
            '{nr}': meta.inspectionNumber.toString(),
            '{datum}': new Date(meta.date).toLocaleDateString(emailLanguage === 'en' ? 'en-US' : 'de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }),
            '{ort}': meta.projectAddress || '',
            '{koordinator}': meta.coordinator || ''
        };

        const replacePlaceholders = (template: string) => {
            let res = template;
            Object.entries(replacements).forEach(([key, val]) => { res = res.replace(new RegExp(key, 'g'), val); });
            return res;
        };

        const defaultSubDE = "Begehungsprotokoll Nr. {nr} - {projekt}";
        const defaultSubEN = "Site Inspection Report No. {nr} - {projekt}";

        const baseSubject = replacePlaceholders(emailLanguage === 'en' ? defaultSubEN : defaultSubDE);
        // Der Kunden-Betreff bleibt auch bei einem Wiederholungsversand sauber;
        // markiert wird nur der Sendebericht (siehe toLogSubject).
        setSubject(baseSubject);
        setMessage(replacePlaceholders(getDefaultEmailBody(emailLanguage, hasOpenDefects(report.items))));
    }
  }, [emailLanguage, isOpen]); // Refires if language is toggled in modal

  // --- SIGNATURE GENERATION ---
  const signaturePreview = useMemo(() => {
      const sigTemplate = settings?.emailSignatureTemplate || '';
      const meta = report.metadata;
      
      // LOGIK-WECHSEL: Wir nutzen PRIMÄR die Daten des eingeloggten Nutzers (userProfile),
      // damit Vertreter (Substitute) mit ihrem eigenen Namen unterschreiben.
      // Fallback auf Projekt-Daten nur, wenn kein Profil geladen ist.

      let fName = '';
      let lName = '';
      let email = '';
      let phone = '';
      let position = 'SiGeKo';

      if (userProfile) {
          // Fall 1: Eingeloggter Nutzer (Vertreter oder Inhaber)
          fName = userProfile.first_name || '';
          lName = userProfile.last_name || '';
          email = userProfile.email || '';
          phone = userProfile.phone || '';
          position = userProfile.job_title || 'SiGeKo';
      } else {
          // Fall 2: Fallback auf statische Projektdaten (sollte selten passieren)
          const nameParts = (meta.coordinator || '').split(' ');
          fName = nameParts[0] || '';
          lName = nameParts.slice(1).join(' ') || '';
          email = meta.coordinatorEmail || '';
          phone = meta.coordinatorPhone || '';
      }

      return sigTemplate
          .replace(/{vorname}/g, fName)
          .replace(/{nachname}/g, lName)
          .replace(/{email}/g, email)
          .replace(/{telefon}/g, phone)
          .replace(/{Position}/g, position);
  }, [settings?.emailSignatureTemplate, report.metadata, userProfile]);

  /**
   * Die Begehungspositionen zur Buchung. Frueher standen hier zusaetzlich die
   * LV-Nummern-Praefixe `2.7.`/`2.8.` — die Nummerierung gehoert aber dem
   * KUNDEN. Das echte Kunden-LV nummeriert seine Begehungen 1.4./2.2./2.5.
   * („Betreuung Baustellen Leitungsbau Los 1 LHT"), waehrend unter 2.7. dort
   * etwas ganz anderes steht. Es gilt dieselbe Regel wie in den drei
   * Dokumentmodulen: Zuordnung fuehrt, Text ist nur das zweite Signal.
   *
   * OHNE fallbackToAllServices: findet sich keine Begehungsposition, bleibt die
   * Liste leer — die Auswahl erscheint dann gar nicht (siehe
   * needsServiceSelection) statt Fahrtkosten als Begehung anzubieten.
   */
  const availableServices = useMemo(() => {
      const services = report.metadata.globalData?.services || [];
      // NEP-Positionen kommen hier nicht an: selectBillableServices laesst sie
      // fuer jeden Modul-Buchungsdialog weg, weil der Versand keine
      // Begruendung abfragen kann. Der Weg dafuer ist der Buchungsdialog im
      // Projekt-Hub (ServiceTrackingModal).
      return selectBillableServices(services, 'inspection');
  }, [report.metadata.globalData]);

  // Gibt es Begehungspositionen, die NUR wegen NEP fehlen? Dann sagt die
  // Rueckfrage beim Versand ohne Buchung, wo die Begehung stattdessen gebucht
  // wird — statt „keine Begehungsposition", was hier schlicht falsch waere.
  const nepInspectionOnly = useMemo(() => {
      const services = report.metadata.globalData?.services || [];
      return services.some(s => s.assignment === 'inspection' && isNepService(s));
  }, [report.metadata.globalData]);

  const billingEnabled = !report.metadata.isExported || forceBilling;

  /**
   * PAUSCHALPROJEKT: gibt es eine Pauschale in der Versandliste
   * (selectBillableServices liefert dort [Pauschale, …nicht gedeckte Lose])?
   * Nicht fuer eine Tochter: deren Begehung bucht SiGeKoInspectionModule auf
   * die Mutter, und die Vorauswahl traegt dann eine Id aus dem LV der MUTTER —
   * die hier umzubiegen buchte ein zweites Mal ins eigene LV.
   */
  const inspectionFlat = useMemo(() => {
      if (report.metadata.globalData?.linkedProjectConfig?.role === 'child') return null;
      return availableServices.find(s => bookingIsFlatProof(s)) ?? null;
  }, [availableServices, report.metadata.globalData?.linkedProjectConfig?.role]);

  /**
   * Eine gespeicherte Vorauswahl (precapturedServiceId, currentBillingSplit)
   * auf eine von der Pauschale GEDECKTE Position — typisch 2.7.10 aus der Zeit
   * vor der Pauschale — faellt auf die Pauschale; ein nicht gedecktes Los
   * bleibt. Dieselbe Regel wie Versandliste und Buchungskern
   * (flatTargetForInspectionChoice / redirectMainBookingsToFlat), damit der
   * Dialog zeigt, was gebucht wird.
   */
  const aufPauschale = (id: string | null | undefined): string | null => {
      if (!id) return null;
      if (!inspectionFlat) return id;
      return flatTargetForInspectionChoice(report.metadata.globalData?.services || [], id) ?? id;
  };

  const effectiveServiceId = aufPauschale(precapturedServiceId || (billingEnabled ? manualServiceId : null));

  /**
   * Die aufgeteilte Buchung (mehrere Lose aus EINER Begehung). Ohne Aufteilung
   * bleibt der Wert leer und es gilt allein `effectiveServiceId` — genau wie
   * bisher. Der persistierte Zwilling `currentBillingSplit` greift, wenn der
   * Dialog ohne die Werte des Ablaufs geoeffnet wird (z. B. aus der
   * Projektuebersicht); er unterliegt derselben isExported-Sperre wie die
   * manuelle Auswahl, damit ein bereits versendeter Bericht nicht nachbucht.
   */
  const effectiveBookings = useMemo<BookingSplitRow[] | undefined>(() => {
      const persisted = report.metadata.currentBillingSplit;
      const rows = precapturedBookings && precapturedBookings.length > 0
          ? precapturedBookings
          : (billingEnabled && persisted && persisted.length > 0 ? persisted : undefined);
      if (!rows || !inspectionFlat) return rows;
      // Pauschalprojekt: gedeckte Lose werden EIN Nachweis auf der Pauschale,
      // nicht gedeckte bleiben Mengenbuchung — dieselbe Umlenkung wie in book().
      return redirectMainBookingsToFlat(rows, report.metadata.globalData?.services || []).rows;
  }, [precapturedBookings, report.metadata.currentBillingSplit, billingEnabled, inspectionFlat, report.metadata.globalData?.services]);

  const needsServiceSelection = billingEnabled && !effectiveServiceId && availableServices.length > 0;

  /**
   * Dieser Versand würde nichts abrechnen — und der Bericht ist auch noch nicht
   * abgerechnet.
   *
   * Genau hier fiel Bericht #13 des Projekts 26041014 aus der Abrechnung: die
   * Mail ging raus, Archiv und Sendebericht entstanden, gebucht wurde nie, und
   * die Begehung fehlte in jeder Rechnung. Ohne Begehungsposition im LV greift
   * needsServiceSelection nicht (es gibt nichts auszuwählen), und ein bereits
   * als exportiert geltender Bericht sperrt die Auswahl ohnehin — beide Wege
   * führten stillschweigend zum selben Ergebnis.
   *
   * Der Versand wird deshalb nicht verhindert (es gibt gute Gründe, einen
   * Bericht ohne Buchung zu verschicken), aber er muss bestätigt werden.
   */
  const sendWouldBookNothing = useMemo(() => {
      if (isReportBooked(getServiceLog(report), report.metadata.inspectionNumber, report.metadata.sectionId)) return false;
      if (effectiveBookings && effectiveBookings.length > 0) return false;
      return !effectiveServiceId;
  }, [report, effectiveBookings, effectiveServiceId]);
  
  useMemo(() => {
      const services = report.metadata.globalData?.services || [];
      return services.find(s => s.id === effectiveServiceId);
  }, [effectiveServiceId, report.metadata.globalData?.services]);

  useEffect(() => {
    if (isOpen && needsServiceSelection && availableServices.length === 1) {
        setManualServiceId(availableServices[0].id);
    }
  }, [isOpen, needsServiceSelection, availableServices]);

  const filteredContacts = useMemo(() => {
    return report.metadata.distributionList.filter(c => {
        if (!c.email || !c.email.includes('@')) return false;
        if (!activeSectionId || activeSectionId === 'all') return true;
        return (c.sectionIds && c.sectionIds.includes(activeSectionId)) || !c.sectionIds || c.sectionIds.length === 0;
    });
  }, [report.metadata.distributionList, activeSectionId]);

  // FIX: Abhängigkeiten reduziert. Wird nur beim Öffnen ausgeführt.
  // Wenn 'report' hier als Dependency steht, resettet sich das Modal beim 'onUpdate' Call (Erfolg) wieder auf 'idle'.
  useEffect(() => {
    if (isOpen) {
        setStep('config');
        setIsGenerating(false);
        isProcessingRef.current = false;
        updateSendStatus('idle');
        setSendMethod('server');
        setForceBilling(false);
        setLastFailedAttemptAt(null);
        setRetryConfirmed(false);
        setSendeFehlerText(null);
        setNichtVersendet(null);
        setPersistVeraltet(false);
        setPdfRueckfall(null);
        setWartetLange(null);
        setBookingNotices([]);
        setFlatRedirectNotice(null);

        const initialSelection = new Set<string>();
        // Verwende report.metadata direkt hier, um die aktuellen Daten beim Öffnen zu haben
        const currentContacts = report.metadata.distributionList.filter(c => {
            if (!c.email || !c.email.includes('@')) return false;
            if (!activeSectionId || activeSectionId === 'all') return true;
            return (c.sectionIds && c.sectionIds.includes(activeSectionId)) || !c.sectionIds || c.sectionIds.length === 0;
        });

        currentContacts.forEach(c => { if (c.emailDistribution) initialSelection.add(c.email); });
        setSelectedEmails(initialSelection);
    }
  }, [isOpen]); // Nur beim Öffnen feuern!

  const toggleRecipient = (email: string) => {
      const newSet = new Set(selectedEmails);
      if (newSet.has(email)) newSet.delete(email); else newSet.add(email);
      setSelectedEmails(newSet);
  };

  const bookingOpts = () => ({
      serviceId: effectiveServiceId,
      mainBookings: effectiveBookings,
      additionalAddons,
      hours: precapturedHours,
      performerId: userProfile?.id || 'system',
      // KEIN Platzhalter 'System' und kein "Max undefined" mehr: Der Wert
      // landet als performerName in der Buchung und von dort in die
      // Koordinatorenspalte des Leistungsnachweises FÜR DEN KUNDEN. Beides ist
      // truthy und verdrängte dort den richtigen Archivnamen. Ein leerer Name
      // lässt den Archivnamen greifen (generatorPerformance.performer).
      performerName: buildActorName(userProfile),
      inspectionNumber: report.metadata.inspectionNumber,
      sectionId: report.metadata.sectionId,
      date: report.metadata.date,
      time: report.metadata.time,
  });

  const executeDbUpdate = async () => {
      setCurrentStep('save');
      // Der Protokollstand VOR dem Versand. Er liefert dem Journal die bereits
      // vergebenen Eintrags-ids und muss deshalb gelesen werden, bevor gebucht
      // wird.
      const previousLog: ServiceTransaction[] = getServiceLog(report);
      const buchung = applyReportBookingWithNotices(report, bookingOpts());
      const { report: bookedReport, skipped, entries: neueEintraege, statements } = buchung;
      // Übersprungene Tagesbuchungen sichtbar machen (siehe reportBooking.ts):
      // eine still verschluckte Buchung fällt erst bei der Rechnung auf.
      setBookingNotices(skipped.filter(s => s.reason !== 'same_report'));
      // Das zentrale Netz in reportBooking (redirectMainBookingsToFlat) hat eine
      // Begehungsposition auf die Pauschale umgelenkt. aufPauschale oben faengt
      // die bekannten Wege schon ab; kam trotzdem eine durch, sagt es der
      // Abschluss statt still anders zu buchen, als gewaehlt war.
      setFlatRedirectNotice(flatRedirectHinweis(buchung.redirectedToFlat, report.metadata.globalData?.services || []));

      // Der Versand ist der Beleg, dass die Begehung stattgefunden hat — also
      // schreibt er auch den Fälligkeitsplan fort. Bisher tat das NUR der
      // Dialog "Nächste Begehung anlegen"; wer den Bericht nur verschickte,
      // ließ den Termin beliebig lange stehen.
      //
      // Kein eigener DB-Write nötig: saveProjectToCloud unten schreibt
      // next_scheduled_date ohnehin aus metadata.intervalConfig, und
      // last_inspection_date aus metadata.date. Es reicht, den Bericht vor dem
      // Speichern zu ergänzen. planDueDateAfterSend ist monoton — ein
      // Wiederholungsversand kann den Plan damit nicht zurückdrehen.
      const dueDate = planDueDateAfterSend(
          bookedReport.metadata.date,
          bookedReport.metadata.intervalConfig?.nextScheduledDate,
          bookedReport.metadata.intervalConfig,
      );
      const updatedReport = dueDate
          ? {
                ...bookedReport,
                metadata: {
                    ...bookedReport.metadata,
                    intervalConfig: { ...bookedReport.metadata.intervalConfig!, nextScheduledDate: dueDate },
                },
            }
          : bookedReport;

      // ── Zweitschrift ins Buchungsjournal: VORMERKEN, bevor gespeichert wird ──
      //
      // Am 07.09.2026 warf saveProjectToCloud bei acht Versendungen ZWISCHEN
      // dem Schreiben des Protokolls (projects.update) und den Positionen
      // (Bildpruefung, leere Datumsfelder). Die Zweitschrift stand damals erst
      // NACH dem Speichern im Code — sie wurde nie aufgerufen, der Ausgangskorb
      // hat nichts davon erfahren, die Zeilen fehlten im Journal bis zum
      // Nachtlauf. Deshalb liegt die Anfrage jetzt VOR dem ersten Schreiben im
      // Korb (`vorgemerkt`). Geschrieben wird sie erst nach dem bestaetigten
      // Speichern (flushJournalMirror unten, gefiltert auf das geschriebene
      // Protokoll). Bricht das Speichern ab, prueft `retryJournalMirrors` gegen
      // den Server: traegt das JSON die Zeile, wird sie angehaengt — sonst
      // verworfen. Eine Journalzeile ohne JSON-Zeile entsteht so nie.
      //
      // `statements` kommt aus der Buchung selbst. Nur hier steht fest, ob
      // eine Zeile die BEGEHUNG deckt oder eine Zusatzleistung ist — der
      // reportRef steht auf beiden und beweist keine Begehung; genau daraus
      // entstand die Klasse „gebucht wurde nur die Zusatzleistung".
      if (projectId && neueEintraege.length > 0) {
          queueJournalMirror({
              projectId,
              entries: neueEintraege,
              previousLog,
              source: 'report_send',
              linkedProjectConfig: report.metadata.globalData?.linkedProjectConfig ?? null,
              statements,
          });
      }

      if (projectId) {
          // Collect the uploaded image URLs while saving so the archival snapshot
          // can store URL references instead of inlining every photo as base64.
          // For large reports this shrinks full_report_json from many MB to a few
          // KB and makes the write far faster. Backwards compatible: older
          // snapshots that still contain base64 keep working unchanged on load.
          const assetSink: { items?: any[]; plans?: any[] } = { items: [], plans: [] };
          await saveProjectToCloud(updatedReport, projectId, undefined, undefined, assetSink);

          // ── Zweitschrift ins Buchungsjournal (S4b) ────────────────────────
          //
          // Erst NACH dem bestaetigten Speichern des JSON: die oben vorgemerkte
          // Anfrage wird jetzt geschrieben, gefiltert auf das Protokoll, das
          // wirklich gespeichert wurde. Ohne `await` und damit ohne Einfluss
          // auf den Ausgang: der Aufrufer (finalizeSend) zeigt seinen
          // catch-Zweig als „Persistenz fehlgeschlagen" an, und eine
          // ausgebliebene Zweitschrift ist kein gescheiterter Versand — sie
          // bleibt im Korb und wird nachgeholt.
          void flushJournalMirror(projectId, getServiceLog(updatedReport));

          await updateProjectExportStatus(projectId, updatedReport.metadata.inspectionNumber);

          const snapshotReport = {
              ...updatedReport,
              items: assetSink.items && assetSink.items.length ? assetSink.items : updatedReport.items,
              metadata: {
                  ...updatedReport.metadata,
                  plans: assetSink.plans && assetSink.plans.length ? assetSink.plans : updatedReport.metadata.plans
              }
          };
          // Versender mitgeben: spart den profiles-Roundtrip und archiviert
          // garantiert den Menschen, der hier tatsächlich versendet — nicht
          // den Hauptverantwortlichen des Projekts.
          setCurrentStep('archive');
          await saveReportSnapshot(snapshotReport, projectId, userProfile, userProfile);  // 4. Argument: der VERSENDER (sent_by) — der Name im Leistungsnachweis
      }

      // Update Frontend State (this causes a re-render)
      if (onUpdate) onUpdate(updatedReport);
  };

  // Nach dem Versand: Sendebericht, Buchung und Archiv sichtbar (Spinner)
  // persistieren. Der Marker bleibt bei Fehlschlag liegen und wird beim
  // nächsten App-Start automatisch nachgeholt (resumePendingSendPersistence).
  const finalizeSend = async (markerId: string | null) => {
      updateSendStatus('persisting');
      try {
          const marker = markerId ? readPendingSendMarkers().find(m => m.id === markerId) : undefined;
          if (marker?.emailLog) {
              setCurrentStep('log');
              await logEmailAttempt(marker.emailLog);
              updatePendingSendMarker(marker.id, { emailLog: null });
          }
          await executeDbUpdate();
          if (markerId) removePendingSendMarker(markerId);
          updateSendStatus('success');
      } catch (persistError) {
          console.error('Persistenz nach Versand fehlgeschlagen:', persistError);
          setPersistVeraltet(istBuildVeraltet() || isBuildGuardWriteError(persistError));
          updateSendStatus('persist_error');
      } finally {
          // Die Endbildschirme (Erfolg / Speichern ausstehend) tragen ihre
          // eigene Aussage — der Fortschritt hat hier nichts mehr zu melden.
          setCurrentStep(null);
      }
  };

  const handleRetryPersist = () => finalizeSend(pendingMarkerId);

  // Wiederholungs-Vermerk NUR für den Sendeverlauf (email_logs): der Kunde
  // bekommt den Betreff immer ohne Präfix. Idempotent — steht das Präfix
  // schon (manuell) im Betreff, wird es nicht gestapelt.
  const toLogSubject = (value: string): string =>
      isRepeatSend && !/^\s*\[Wiederholung\]/i.test(value) ? `[Wiederholung] ${value}` : value;

  const handleProcess = async () => {
      // Zweiter Riegel neben dem ersetzten Knopf: handleProcess ist auch über
      // die Tastatur und über einen wiederaufgenommenen Versand erreichbar.
      if (istUebung) { alert(UEBUNG_BLOCK.warning); return; }

      if (deliveryConfig?.method !== 'poolarserver' && selectedEmails.size === 0) {
          alert("Bitte wählen Sie mindestens einen Empfänger aus."); 
          return; 
      }
      
      if (needsServiceSelection) {
          alert("Bitte wählen Sie eine Leistungsposition für die Abrechnung aus.");
          return;
      }

      // Bewusste Entscheidung statt stiller Lücke — siehe sendWouldBookNothing.
      if (sendWouldBookNothing && !window.confirm(
          `Achtung: Für Bericht #${report.metadata.inspectionNumber} wird KEINE Leistung abgerechnet.\n\n`
          + `Die Begehung wird dadurch auf keiner Rechnung erscheinen. `
          + `${availableServices.length === 0
                ? (nepInspectionOnly
                    ? 'Die Begehungsposition ist eine NEP-Position (nicht beauftragt). Buchen Sie die Begehung mit Begründung im Projekt-Hub unter „Leistung & Budget".'
                    : 'Im Leistungsverzeichnis dieses Projekts gibt es keine Begehungsposition.')
                : 'Der Bericht gilt bereits als abgerechnet; über "Erneut abrechnen" lässt sich das ändern.'}\n\n`
          + `Trotzdem versenden?`)) {
          return;
      }

      // Der Riegel gegen doppelte Sendeberichte, den der Ref-Lock unten NICHT
      // stellen kann: Er deckt nur den Doppelklick in diesem Tab ab. Die zweite
      // Zeile in Projekt 26041014 kam aus einem der anderen Wege — Replay des
      // Pending-Markers in einem zweiten Tab, Wiederhol-Klick auf der
      // Persistenz-Fehlerseite, Netz-Retry. Alle diese Wege führen denselben
      // Schlüssel mit (über den Marker bzw. über emailLogPayload), sodass die
      // Datenbank die zweite Zeile verwirft statt sie anzulegen.
      //
      // Ein Schlüssel je Durchlauf reicht, weil ein Durchlauf höchstens EINE
      // Log-Zeile schreibt: entweder den Erfolgseintrag (Marker oder
      // Direkt-Insert) oder — wenn sendReportEmail wirft — den Fehlereintrag.
      // Wer nach einem Fehlversuch erneut sendet, ruft handleProcess neu auf und
      // bekommt hier einen neuen Schlüssel.
      //
      // ANKER: Steht bewusst VOR dem Ref-Lock, nicht danach — würde newSendKey()
      // werfen, während der Lock schon auf true steht, liefe weder catch noch
      // finally, und der Senden-Knopf bliebe ohne Meldung tot. Diese Zeile muss
      // eine funktionslokale const bleiben, die bei JEDEM Aufruf von
      // handleProcess neu ausgeführt wird — NIEMALS in den Komponenten-Rumpf,
      // useState, useMemo oder useRef hochziehen. Sonst teilten sich alle
      // Sendevorgänge dieser Sitzung einen einzigen Schlüssel, und der
      // Unique-Index in email_logs würde jeden weiteren echten Sendebericht
      // als vermeintliches Duplikat verschlucken.
      const sendKey = newSendKey();

      // Ref-Lock vor dem State: der Render-State isGenerating steht erst nach
      // dem nächsten Re-Render, ein zweiter Klick davor käme sonst durch.
      if (isProcessingRef.current) return;
      isProcessingRef.current = true;

      setIsGenerating(true);
      updateSendStatus('idle');
      setRetryConfirmed(false);
      setSendeFehlerText(null);
      setNichtVersendet(null);
      setPdfRueckfall(null);
      setWartetLange(null);
      // Bis sendReportEmail aufgerufen wird, ist nichts hinausgegangen; ab dann
      // ist es nach einem Fehler offen (Abbruch nach dem Absetzen). Daran
      // hängen nur Text und Bestätigungszwang — Marker und Riegel bleiben.
      let sendenAngestossen = false;
      let gesendet = false;
      let pdfFertig = false;

      try {
          // Veralteter Tab: NICHT senden. Er darf nicht speichern
          // (saveProjectToCloud → istBuildVeraltet), die Mail ginge also
          // hinaus, Abrechnung und Archiv nicht (Vorfall 6051 #149,
          // 28.09.2026). Gilt für alle drei Wege (Server, Poolarserver,
          // lokal): jeder speichert erst NACH der Ausgabe. Die Prüfung fragt
          // /version.json sofort (höchstens 3 s) statt auf den
          // Fünf-Minuten-Herzschlag des Gates zu warten.
          // `return` läuft durch das finally unten: Lock frei, Knopf wieder aktiv.
          if (await istVorVersandVeraltet()) {
              setNichtVersendet(VERALTET_NICHT_VERSENDET);
              return;
          }

          const meta = report.metadata;
          // The DE/EN switch in this dialog only controls the e-mail text (subject/body).
          // The PDF follows the report's own language (report.metadata.reportLanguage),
          // so toggling the e-mail language does not trigger a (slow) report re-translation.
          setCurrentStep('pdf');
          // Der Versender ist der Durchfuehrende: weicht er vom Hauptverantwortlichen
          // ab, weist ihn das Protokoll aus (Vertretung / Urlaub).
          // Freimelde-Link fuer genau diesen Bericht: idempotent, ein Wiederholungs-
          // versand liefert denselben Token. Schlaegt es fehl (Migration offen,
          // Freelancer, kein Netz), ist das Ergebnis null — das PDF geht dann ohne
          // QR-Kasten raus, der Versand scheitert nie daran.
          // Mit Ausweg wie das PDF: hängt die Vorbereitung, ist sicher nichts hinausgegangen.
          const freimeldungQr = projectId ? await mitAusweg(bereiteFreimeldungLinkVor(projectId, report, window.location.origin, window.location.pathname), wartenBeendenRef) : null;
          const doc = await mitAusweg(generatePDF(report, settings, undefined, undefined, { conductedByName: buildActorName(userProfile), freimeldungQr }), wartenBeendenRef);
          pdfFertig = true;
          const defaultTemplate = emailLanguage === 'en' ? "{datum}_{projekt}_Report_No_{nr}" : "{datum}_{projekt}_Bericht_Nr_{nr}";
          let tmpl = settings?.filenameTemplate || defaultTemplate;
          const filenameReplacements: Record<string, string> = {
            '{projekt}': (meta.projectName || 'Projekt').replace(/[^a-z0-9äöüß-]/gi, '_'),
            '{nr}': (meta.inspectionNumber || 0).toString(),
            '{datum}': (meta.date ? new Date(meta.date) : new Date()).toISOString().split('T')[0].replace(/-/g, '_'),
            '{ort}': (meta.projectAddress || '').split(',')[0].replace(/[^a-z0-9äöüß-]/gi, '_')
          };
          Object.entries(filenameReplacements).forEach(([key, val]) => { tmpl = tmpl.replace(new RegExp(key, 'g'), val); });
          const filename = tmpl.endsWith('.pdf') ? tmpl : `${tmpl}.pdf`;
          // Ensure filename has no spaces or special characters for Supabase storage
          const safeFilename = filename
            .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
            .replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue')
            .replace(/ß/g, 'ss')
            .replace(/[^a-zA-Z0-9_.-]/g, '_');

          // Use the pre-calculated signature (Dynamic User)
          const finalHtml = `
            <div style="font-family: sans-serif; color: #334155; line-height: 1.6;">
              <p>${message.replace(/\n/g, '<br>')}</p>
              <br>
              ${signaturePreview}
            </div>
          `;
          
          // Determine the correct sender name for email header
          // Ein Profil ohne gepflegte Namensfelder ergab hier '' bzw. ein
          // Fragment und schickte die Mail ohne Absendernamen — dann lieber der
          // Koordinator aus den Metadaten.
          const senderName = buildActorName(userProfile) || meta.coordinator;
          
          const replyTo = userProfile?.email || meta.coordinatorEmail;

          if (deliveryConfig?.method === 'poolarserver') {
              // Android-App: Blob-Downloads kommen in der WebView nicht an → ERST
              // Teilen-Dialog, DANN Poolarserver im System-Browser (appPdfDannPoolar).
              // Browser: unverändert.
              if (istNativeApp()) {
                  void appPdfDannPoolar(doc.output('blob'), filename, deliveryConfig.poolarserverUrl);
              } else {
                  doc.save(filename);
                  if (deliveryConfig.poolarserverUrl) window.open(deliveryConfig.poolarserverUrl, '_blank');
              }
              let markerId: string | null = null;
              if (projectId) {
                  markerId = addPendingSendMarker({ projectId, booking: bookingOpts(), emailLog: null })?.id ?? null;
                  if (markerId) setPendingMarkerId(markerId);
              }
              await finalizeSend(markerId);
          } else if (sendMethod === 'server') {
               try {
                   // PDF als Blob generieren und hochladen (um 413 Content Too Large zu vermeiden)
                   setCurrentStep('upload');
                   const pdfBlob = doc.output('blob');
                   const pdfUrl = await mitAusweg(uploadPdf(pdfBlob, safeFilename), wartenBeendenRef);

                   setCurrentStep('send');
                   sendenAngestossen = true;
                   // „Warten beenden“ bricht den Request WIRKLICH ab (AbortController).
                   // Liefe er weiter, käme ein später Erfolg ins Leere — oder er
                   // schriebe den Stand vom Klick über alles, was danach erfasst
                   // wurde. So ist der Ausgang offen wie bei jedem Netzabbruch:
                   // Fehlerzweig, erneut senden nur mit Bestätigung.
                   const abbruch = new AbortController();
                   const beenden = () => abbruch.abort(wartenBeendetFehler());
                   wartenBeendenRef.current = beenden;
                   await sendReportEmail({
                       recipients: Array.from(selectedEmails),
                       subject,
                       html: finalHtml,
                       pdfUrl,
                       filename: safeFilename,
                       senderName: senderName,
                       replyTo: replyTo
                   }, abbruch.signal).finally(() => {
                       if (wartenBeendenRef.current === beenden) wartenBeendenRef.current = null;
                   });
                   gesendet = true;
                   
                   if (deliveryConfig?.method === 'both') {
                       // Android-App: ERST Teilen-Dialog, DANN System-Browser (s. o.).
                       if (istNativeApp()) {
                           void appPdfDannPoolar(doc.output('blob'), filename, deliveryConfig.poolarserverUrl);
                       } else {
                           doc.save(filename);
                           if (deliveryConfig.poolarserverUrl) window.open(deliveryConfig.poolarserverUrl, '_blank');
                       }
                   }

                   // E-Mail ist raus. Ab hier darf nichts mehr verloren gehen:
                   // Sendebericht, Buchung und Archiv laufen sichtbar (Spinner) und
                   // sind über einen localStorage-Marker abgesichert, den der nächste
                   // App-Start nachspielt, falls die App vorher beendet wird.
                   const emailLogPayload = {
                       project_id: projectId || '',
                       project_name: meta.projectName || '',
                       recipients: Array.from(selectedEmails).join(', '),
                       subject: toLogSubject(subject),
                       status: 'success' as const,
                       body_html: finalHtml,
                       pdf_url: pdfUrl,
                       // Wandert mit dem Payload in den localStorage-Marker und
                       // von dort in den Replay. Nur so schreiben Direkt-Insert,
                       // finalizeSend und resumePendingSendPersistence
                       // nachweislich DIESELBE Zeile.
                       send_key: sendKey
                   };
                   let markerId: string | null = null;
                   if (projectId) {
                       markerId = addPendingSendMarker({ projectId, booking: bookingOpts(), emailLog: emailLogPayload })?.id ?? null;
                       if (markerId) setPendingMarkerId(markerId);
                   }
                   if (!markerId) {
                       // Kein (schreibbarer) Marker: Sendebericht sofort selbst sichern.
                       try { await logEmailAttempt(emailLogPayload); } catch (logError) { console.error('Sendebericht konnte nicht gespeichert werden:', logError); }
                   }
                   await finalizeSend(markerId);

               } catch (serverError: any) {
                   console.error("Sending Error:", serverError);
                   // Den NetzFehler wirft sendReportEmail nur VOR dem fetch (Sitzung
                   // ohne Netz nicht erneuerbar): dann ging sicher nichts hinaus.
                   const vorDemAbschicken = !gesendet && fehlerName(serverError) === 'NetzFehler';
                   if (sendenAngestossen && !vorDemAbschicken) {
                       // Im Senden gescheitert: ob die Mail hinausging, ist offen —
                       // erneut nur nach Bestätigung (wie bisher), mit dem Servertext.
                       updateSendStatus('error');
                       setLastFailedAttemptAt(new Date().toISOString());
                       setSendeFehlerText(gesendet
                           ? `Die E-Mail ist hinausgegangen, danach trat ein Fehler auf: ${fehlerText(serverError) || 'unbekannt'}`
                           : serverFehlerText(serverError));
                   } else {
                       // Hochladen gescheitert bzw. vor dem Abschicken: es ging nichts hinaus.
                       setNichtVersendet(nichtVersendetMeldung(sendenAngestossen ? 'vorbereitung' : 'upload', serverError));
                   }
                   logEmailAttempt({
                       project_id: projectId || '',
                       project_name: meta.projectName || '',
                       recipients: Array.from(selectedEmails).join(', '),
                       subject: toLogSubject(subject),
                       status: 'error',
                       error_message: serverError.message,
                        body_html: finalHtml,
                        pdf_url: undefined,
                        // Derselbe Schlüssel wie oben, und das ist kein
                        // Versehen: In EINEM Durchlauf entsteht entweder der
                        // Erfolgs- ODER dieser Fehlereintrag, nie beides
                        // (finalizeSend fängt seine Fehler selbst ab und wirft
                        // nicht hierher). Der Schlüssel schützt damit genau den
                        // Fall, den er soll — den mehrfach abgesetzten Insert
                        // desselben Fehlversuchs.
                        send_key: sendKey
                     }).catch(logError => console.error('Fehlversuch-Log konnte nicht gespeichert werden:', logError));
                   // Die Meldung steht im Dialog (roter bzw. gelber Kasten) — kein Browser-Hinweis;
                   // der Kasten nennt auch das Rückfall-PDF (pdfRueckfall). Ist der
                   // Dialog schon zu (Versand lief im Hintergrund), kein PDF aus dem
                   // Nichts — der Fehlversuch steht im Sendeverlauf.
                   if (sichtbarRef.current) {
                       if (istNativeApp()) {
                           void dateiAusgeben(doc.output('blob'), filename).catch(ausgabeFehlerMelden);
                           setPdfRueckfall('geteilt');
                       } else {
                           doc.save(filename);
                           setPdfRueckfall('geladen');
                       }
                   }
               }
          } else {
              // Local / Outlook Path
              // App (Vertrag E19): EIN Mail-Entwurf mit PDF statt Teilen-Dialog
              // UND mailto (das ergab zwei getrennte Entwürfe). Das PDF geht schon
              // hier in den App-Cache, parallel zur Persistenz; übergeben wird es
              // unten an der Stelle, an der der Browser mailto öffnet.
              let appPdfUri: Promise<string | null> | null = null;
              if (istNativeApp()) {
                  appPdfUri = dateiInCacheSchreiben(doc.output('blob'), filename)
                      .catch((e: unknown) => { ausgabeFehlerMelden(e); return null; });
              } else {
                  doc.save(filename);
              }
              // Marker sichert Buchung/Archiv ab, falls die App zwischen Speichern
              // und Abschluss beendet wird; der nächste App-Start holt es nach.
              const localMarkerId = projectId
                  ? (addPendingSendMarker({ projectId, booking: bookingOpts(), emailLog: null })?.id ?? null)
                  : null;
              if (localMarkerId) setPendingMarkerId(localMarkerId);
              updateSendStatus('persisting');
              try {
                  await executeDbUpdate();
                  if (localMarkerId) removePendingSendMarker(localMarkerId);
              } catch (persistError) {
                  console.error('Persistenz vor lokalem Versand fehlgeschlagen:', persistError);
                  if (istBuildVeraltet() || isBuildGuardWriteError(persistError)) {
                      // Hier wird VOR mailto gespeichert: es ist nichts
                      // hinausgegangen. Der Marker darf deshalb nichts
                      // nachholen — er buchte und archivierte sonst nach dem
                      // Neuladen einen Bericht, den der Kunde nie bekam.
                      // Stattdessen wie die Sperre vor dem Senden: neu laden,
                      // erneut senden.
                      if (localMarkerId) removePendingSendMarker(localMarkerId);
                      updateSendStatus('idle');
                      setNichtVersendet(VERALTET_NICHT_VERSENDET);
                      return;
                  }
                  updateSendStatus('persist_error');
                  // App: der Browser hat das PDF an dieser Stelle schon heruntergeladen
                  // (mailto kommt dort auch nicht) — hier es wenigstens teilen.
                  if (appPdfUri) void appPdfNurTeilen(appPdfUri, filename);
                  return;
              }
              // Schritt UND Status im selben Render umschalten: würde erst der
              // Status auf 'idle' zurückfallen und der Schritt bliebe bis zum
              // setTimeout auf 'config', stünde der Senden-Button eine Sekunde
              // lang wieder scharf — genau das Fenster für den Doppelversand.
              setStep('instruction');
              updateSendStatus('idle');
              if (appPdfUri) {
                  // App: EIN Entwurf mit denselben Empfängern, Betreff und Text wie
                  // der mailto unten — KEIN mailto (Rückfall nur ohne emailEntwurf).
                  void appMailUebergeben(appPdfUri, filename, Array.from(selectedEmails), subject, message);
                  return;
              }
              setTimeout(() => {
                  const recipients = Array.from(selectedEmails).join('; ');
                  window.location.href = `mailto:${recipients}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
              }, 1000);
          }
      } catch (error) {
          console.error("General Process Error:", error);
          setIsGenerating(false);
          if (!sendenAngestossen) {
              // PDF bzw. Vorbereitung gescheitert: sendReportEmail lief nie.
              setNichtVersendet(nichtVersendetMeldung(pdfFertig ? 'vorbereitung' : 'pdf', error));
          } else {
              alert(`Fehler bei der Verarbeitung.`);
          }
      } finally {
          setCurrentStep(null);
          wartenBeendenRef.current = null;
          isProcessingRef.current = false;
          // Nur freigeben, wenn der Senden-Button überhaupt wieder sichtbar ist
          // (idle/error). Bei success/persisting/persist_error zeigt der Dialog
          // eigene Screens — ein "freigegebener" Button wäre dort ein
          // Doppelversand in Wartestellung. Gelesen wird die Ref, weil der
          // State-Wert hier aus der Closure vom Aufrufzeitpunkt stammt.
          const status = serverSendStatusRef.current;
          if (status === 'idle' || status === 'error') {
              setIsGenerating(false);
          }
      }
  };

  /**
   * NUR App, lokaler/Outlook-Weg (Vertrag E19): PDF + Mail als EIN Entwurf an
   * die Mail-App. Rückfall (alte APK): erst Teilen abwarten, dann mailto —
   * Empfänger, Betreff und Text exakt wie im Browser-mailto. Reine Ausgabe:
   * Status, Marker und Persistenz sind zu diesem Zeitpunkt abgeschlossen.
   */
  const appMailUebergeben = async (pdfUri: Promise<string | null>, dateiname: string, an: string[], betreff: string, text: string) => {
      setAppMailUebergabe('laeuft');
      const uri = await pdfUri;
      const weg = await mailEntwurfMitDatei({ an, betreff, text }, uri, dateiname, () => {
          window.location.href = `mailto:${an.join('; ')}?subject=${encodeURIComponent(betreff)}&body=${encodeURIComponent(text)}`;
      });
      setAppMailUebergabe(uri ? weg : 'ohne_pdf');
  };

  /**
   * NUR App, Poolarserver bzw. „both": ERST das PDF über den Teilen-Dialog
   * ausgeben und abwarten (Fehler sichtbar über ausgabeFehlerMelden), DANN den
   * Poolarserver im System-Browser öffnen — gleichzeitig gestartet, verdeckt
   * der eine den anderen. Läuft neben dem Versand her: Status, Marker und
   * Persistenz warten nicht darauf (wie zuvor `void dateiAusgeben(…)`).
   */
  const appPdfDannPoolar = async (pdf: Blob, dateiname: string, poolarUrl: string | undefined) => {
      try {
          await dateiAusgeben(pdf, dateiname);
      } catch (e) {
          ausgabeFehlerMelden(e);
      }
      if (poolarUrl) externOeffnen(poolarUrl);
  };

  /** NUR App: PDF aus dem Cache teilen, wenn nach dem Speichern-Fehler kein Entwurf folgt. */
  const appPdfNurTeilen = async (pdfUri: Promise<string | null>, dateiname: string) => {
      const uri = await pdfUri;
      if (uri) await dateiTeilen(uri, dateiname).catch(ausgabeFehlerMelden);
  };

  const handleFinish = () => { if (onFinished) onFinished(); else onClose(); };
  
  // SENDER DISPLAY INFO
  // Wie senderName oben: Fragment vermeiden, sonst steht im Dialog „Max
  // undefined" als Absender.
  const senderDisplayName = buildActorName(userProfile) || report.metadata.coordinator;
      
  const senderRole = userProfile?.job_title || 'SiGeKo';

  // Ein Schritt läuft (PDF, Upload, Senden, Nachspeichern): kein Schließen —
  // wer jetzt schließt, hielte den Versand für abgebrochen, er läuft aber weiter.
  // Hängt PDF, Upload oder Senden, führt „Warten beenden" hinaus (wartetLange).
  const versandLaeuft = currentStep !== null || serverSendStatus === 'persisting'
      || (isGenerating && (serverSendStatus === 'idle' || serverSendStatus === 'error'));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-scale-in flex flex-col max-h-[90vh]">
            <div className="bg-slate-900 text-white p-5 flex justify-between items-center shrink-0">
                <div className="flex flex-col">
                    <h3 className="font-bold text-lg flex items-center gap-2"><Mail className="w-5 h-5 text-blue-400" /> {emailLanguage === 'en' ? 'Send Report' : 'Bericht versenden'}</h3>
                    {activeSectionId && activeSectionId !== 'all' && <span className="text-[11px] text-blue-200 flex items-center gap-1 mt-1"><Layers className="w-3 h-3" /> {emailLanguage === 'en' ? 'Filtered by section' : 'Gefiltert nach Abschnitt'}</span>}
                </div>
                <div className="flex items-center gap-4">
                    <div className="flex bg-slate-800 p-1 rounded-lg border border-slate-700">
                        <button onClick={() => setEmailLanguage('de')} className={`px-2 py-1 rounded text-[10px] font-black uppercase transition-all ${emailLanguage !== 'en' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-300'}`}>DE</button>
                        <button onClick={() => setEmailLanguage('en')} className={`px-2 py-1 rounded text-[10px] font-black uppercase transition-all ${emailLanguage === 'en' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-300'}`}>EN</button>
                    </div>
                    {versandLaeuft ? (
                        <span className="flex items-center gap-1.5 text-[11px] font-bold text-slate-300"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Läuft …</span>
                    ) : serverSendStatus !== 'success' && (
                        // 44 px Trefferfläche (Handschuh), der negative Rand hält die Kopfzeile wie bisher.
                        <button onClick={onClose} aria-label="Schließen" className="hover:bg-white/10 p-3 -m-2 rounded-lg transition-colors"><X className="w-5 h-5"/></button>
                    )}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
                {currentStep && stepIndex >= 0 ? (
                    <div className="py-6 max-w-md mx-auto">
                        <div className="flex flex-col items-center text-center mb-6">
                            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4 text-indigo-600">
                                <Loader2 className="w-8 h-8 animate-spin" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-800">{SEND_STEP_LABELS[currentStep].title}</h3>
                            <p className="text-sm text-slate-500 mt-1">{SEND_STEP_LABELS[currentStep].hint}</p>
                        </div>

                        <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-indigo-600 rounded-full transition-all duration-500 ease-out relative overflow-hidden"
                                style={{ width: `${stepPercent}%` }}
                            >
                                {/* Der laufende Schritt kann lange dauern (große PDFs, langsames
                                    Netz). Die Bewegung zeigt, dass etwas passiert, ohne einen
                                    Fortschritt zu behaupten, den wir nicht messen können. */}
                                <div className="absolute inset-0 bg-white/30 animate-pulse" />
                            </div>
                        </div>
                        <p className="text-[11px] text-slate-400 text-right mt-1.5 mb-5">
                            Schritt {stepIndex + 1} von {sendSteps.length}
                        </p>

                        {renderStepList(stepIndex)}

                        <div className="mt-6 text-center space-y-1">
                            {mailAlreadySent && (
                                <p className="text-xs font-bold text-emerald-700">
                                    ✓ Die E-Mail ist versendet. Es fehlen nur noch Buchung und Archiv.
                                </p>
                            )}
                            <p className="text-xs text-slate-500">Bitte die App jetzt nicht schließen.</p>
                        </div>

                        {/* Ausweg, wenn PDF, Upload oder Senden hängen (siehe wartetLange). */}
                        {wartenBeendbar && (
                            <div className="mt-5 bg-amber-50 border-2 border-amber-200 p-4 rounded-xl space-y-3">
                                <div className="flex items-start gap-2">
                                    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                                    <div className="space-y-1 min-w-0">
                                        <p className="text-xs font-bold text-amber-800">Dauert ungewöhnlich lange.</p>
                                        <p className="text-[11px] text-amber-700 leading-relaxed">
                                            {currentStep === 'send'
                                                ? 'Keine Antwort vom Mailserver. Warten beenden bricht die Verbindung ab – ob die E-Mail schon hinaus ist, bleibt dann offen; erneut senden geht danach nur mit Bestätigung.'
                                                : 'Bei schwachem Netz kann das hängen. Warten beenden ist sicher: bisher ist nichts hinausgegangen.'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => wartenBeendenRef.current?.()}
                                    className="w-full min-h-[44px] px-4 bg-white border border-amber-300 text-amber-800 rounded-xl text-sm font-semibold hover:bg-amber-100 active:bg-amber-100 transition-colors"
                                >
                                    Warten beenden
                                </button>
                            </div>
                        )}
                    </div>
                ) : serverSendStatus === 'persisting' ? (
                    <div className="flex flex-col items-center justify-center text-center py-8">
                        <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mb-6 text-indigo-600"><Loader2 className="w-10 h-10 animate-spin" /></div>
                        <h3 className="text-xl font-bold text-slate-800">Daten werden gespeichert…</h3>
                        <p className="text-slate-600 mt-2 max-w-sm">Abrechnung, Archiv und Sendebericht werden gespeichert. Bitte die App jetzt nicht schließen.</p>
                    </div>
                ) : serverSendStatus === 'persist_error' ? (
                    <div className="flex flex-col items-center justify-center text-center py-8">
                        <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mb-6 text-amber-600"><AlertTriangle className="w-10 h-10" /></div>
                        <h3 className="text-xl font-bold text-slate-800">Versendet — Speichern ausstehend</h3>
                        {persistVeraltet ? (
                            <>
                                {/* Erneut speichern scheitert aus diesem Tab immer wieder
                                    (istBuildVeraltet gilt bis zum Neuladen) — der Knopf
                                    fehlt deshalb. Der Nachhol-Marker liegt im Gerät; der
                                    neu geladene Tab holt Abrechnung und Archiv nach. */}
                                <p className="text-slate-600 mt-2 mb-6 max-w-sm">Der Bericht wurde versendet. Abrechnung und Archiv sind noch nicht gespeichert, weil inzwischen eine neue Programmversion bereitsteht und dieses Fenster nicht mehr speichern darf. Bitte schließen und oben im Balken „Jetzt aktualisieren“ tippen – danach werden Abrechnung und Archiv auf diesem Gerät automatisch nachgeholt. Nicht erneut versenden.</p>
                                <Button variant="primary" onClick={handleFinish} className="w-full">Schließen</Button>
                            </>
                        ) : (
                            <>
                                <p className="text-slate-600 mt-2 mb-6 max-w-sm">Der Bericht wurde versendet, aber Abrechnung, Archiv und Sendebericht konnten noch nicht gespeichert werden. Jetzt erneut versuchen — andernfalls wird es beim nächsten App-Start automatisch nachgeholt.</p>
                                <Button variant="primary" onClick={handleRetryPersist} className="w-full"><RefreshCw className="w-4 h-4 mr-2" /> Erneut speichern</Button>
                                <Button variant="ghost" onClick={handleFinish} className="w-full mt-2">Später automatisch nachholen</Button>
                            </>
                        )}
                    </div>
                ) : serverSendStatus === 'success' ? (
                    <div className="flex flex-col items-center justify-center text-center py-8">
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6 text-green-600 animate-scale-in"><CheckCircle2 className="w-10 h-10" /></div>
                        <h3 className="text-xl font-bold text-slate-800">
                            {deliveryConfig?.method === 'poolarserver' ? 'PDF generiert & bereit' : 'Erfolgreich versendet!'}
                        </h3>
                        <p className="text-slate-600 mt-2 mb-6 max-w-sm">
                            {deliveryConfig?.method === 'poolarserver'
                                ? (istNativeApp()
                                    ? 'Das PDF wurde zum Speichern/Teilen übergeben und der Poolarserver im Browser geöffnet. Die Abrechnung wurde aktualisiert und ein Snapshot archiviert.'
                                    : 'Das PDF wurde heruntergeladen und der Poolarserver geöffnet. Die Abrechnung wurde aktualisiert und ein Snapshot archiviert.')
                                : 'Der Bericht wurde versandt, die Abrechnung aktualisiert und ein Snapshot archiviert.'}
                        </p>

                        {/* Die abgehakte Liste bleibt stehen: Sie belegt Schritt für
                            Schritt, was tatsächlich durchgelaufen ist. */}
                        <div className="w-full bg-emerald-50/60 border border-emerald-200 rounded-xl p-4 mb-6 text-left">
                            {renderStepList(null)}
                        </div>

                        {bookingNotices.length > 0 && (
                            <div className="w-full bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left flex items-start gap-2">
                                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                                <div className="space-y-1">
                                    <p className="text-[11px] font-bold text-amber-800 uppercase tracking-wider">Abrechnung: Buchung übersprungen</p>
                                    {bookingNotices.map((n, i) => (
                                        <p key={i} className="text-[11px] text-amber-800 leading-relaxed">{n.message}</p>
                                    ))}
                                </div>
                            </div>
                        )}
                        {flatRedirectNotice && (
                            <div className="w-full bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-6 text-left flex items-start gap-2">
                                <Layers className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
                                <p className="text-[11px] text-indigo-800 leading-relaxed">{flatRedirectNotice}</p>
                            </div>
                        )}
                        <Button variant="primary" onClick={handleFinish} className="w-full">Schließen</Button>
                    </div>
                ) : (
                    <>
                        {step === 'config' && (
                            <div className="space-y-6">
                                {serverSendStatus === 'error' && (
                                    <div className="bg-red-50 border-2 border-red-200 p-4 rounded-xl space-y-3">
                                        <div className="flex items-start gap-2">
                                            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                                            <div className="space-y-1">
                                                <p className="text-xs font-bold text-red-800">
                                                    Versuch um {new Date(lastFailedAttemptAt || Date.now()).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr fehlgeschlagen
                                                </p>
                                                {sendeFehlerText && (
                                                    <p className="text-[11px] font-bold text-red-800 leading-relaxed break-words">{sendeFehlerText}</p>
                                                )}
                                                <p className="text-[11px] text-red-700 leading-relaxed">
                                                    Ein Fehler heißt nicht zwingend, dass nichts raus ist: bricht die Verbindung nach dem Absetzen ab,
                                                    ist die E-Mail beim Empfänger trotzdem angekommen. Bitte zuerst im <b>Sendeverlauf</b> (Projekt-Zentrale → Ablage → Sendeverlauf) prüfen,
                                                    ob die E-Mail nicht doch zugestellt wurde, bevor du erneut sendest.
                                                </p>
                                                {pdfRueckfall && (
                                                    <p className="text-[11px] text-red-700 leading-relaxed">{PDF_RUECKFALL_TEXT[pdfRueckfall]}</p>
                                                )}
                                            </div>
                                        </div>
                                        <label className="flex items-center gap-2 cursor-pointer bg-white border border-red-200 rounded-lg px-3 py-2">
                                            <input
                                                type="checkbox"
                                                checked={retryConfirmed}
                                                onChange={(e) => setRetryConfirmed(e.target.checked)}
                                                className="w-4 h-4 accent-red-600"
                                            />
                                            <span className="text-[11px] font-bold text-red-800">Geprüft — trotzdem erneut senden</span>
                                        </label>
                                    </div>
                                )}

                                {/* Vor dem Senden gescheitert: sicher nichts hinausgegangen — keine Bestätigung nötig. */}
                                {nichtVersendet && serverSendStatus === 'idle' && (
                                    <div className="bg-amber-50 border-2 border-amber-200 p-4 rounded-xl flex items-start gap-2">
                                        <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                                        <div className="space-y-1 min-w-0">
                                            <p className="text-xs font-bold text-amber-800">{nichtVersendet.text}</p>
                                            {nichtVersendet.detail && (
                                                <p className="text-[11px] text-amber-700 leading-relaxed break-words">{nichtVersendet.detail}</p>
                                            )}
                                            <p className="text-[11px] text-amber-700 leading-relaxed">{nichtVersendet.hinweis}</p>
                                            {pdfRueckfall && (
                                                <p className="text-[11px] text-amber-700 leading-relaxed">{PDF_RUECKFALL_TEXT[pdfRueckfall]}</p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {needsServiceSelection && availableServices.length > 0 && (
                                    <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl space-y-3">
                                        <label className="text-[11px] font-bold text-blue-700 uppercase block mb-1">Leistung wählen:</label>
                                        <select className="w-full text-xs p-2 rounded border border-blue-200 outline-none" value={manualServiceId} onChange={(e) => setManualServiceId(e.target.value)}>
                                            <option value="">-- Keine Auswahl --</option>
                                            {availableServices.map(s => <option key={s.id} value={s.id}>{s.title} ({s.quantityUsed}/{s.quantityContracted})</option>)}
                                        </select>
                                    </div>
                                )}

                                <div>
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-3">Empfänger ({selectedEmails.size})</label>
                                    <div className="border border-slate-200 rounded-xl overflow-hidden max-h-48 overflow-y-auto" data-tutorial="versand.empfaenger">
                                        {filteredContacts.map(c => (
                                            <div key={c.id} onClick={() => toggleRecipient(c.email)} className={`flex items-center gap-3 p-3 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 transition-colors ${selectedEmails.has(c.email) ? 'bg-blue-50/50' : ''}`}>
                                                <div className={`w-5 h-5 rounded border flex items-center justify-center ${selectedEmails.has(c.email) ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 bg-white'}`}>{selectedEmails.has(c.email) && <CheckCircle2 className="w-3 h-3" />}</div>
                                                <div className="flex-1"><div className="text-sm font-bold text-slate-700">{c.name} <span className="font-normal text-slate-500">({c.company})</span></div><div className="text-xs text-slate-400">{c.email}</div></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    {/* SENDER IDENTITY VISUAL */}
                                    <div className="flex items-center gap-3 bg-slate-50 p-2.5 rounded-lg border border-slate-200" data-tutorial="versand.absender">
                                        <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 border border-indigo-200">
                                            <PenTool className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1">
                                            <span className="text-[11px] uppercase font-bold text-slate-400 block tracking-wider">Unterzeichnet als</span>
                                            <span className="text-xs font-bold text-slate-800">{senderDisplayName} <span className="font-normal text-slate-500">({senderRole})</span></span>
                                        </div>
                                    </div>

                                    <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">Betreff</label><input type="text" name="subject" autoComplete="off" value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg font-bold text-slate-700 focus:ring-2 focus:ring-blue-500 outline-none" data-tutorial="versand.betreff" /></div>
                                    <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">Nachricht</label><textarea rows={6} name="message" autoComplete="off" value={message} onChange={(e) => setMessage(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-600 focus:ring-2 focus:ring-blue-500 outline-none resize-none font-mono" /></div>
                                    
                                    {/* SIGNATURE PREVIEW (Server Mode Only) */}
                                    {sendMethod === 'server' && (
                                        <div>
                                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">Signatur Vorschau</label>
                                            <div className="border border-slate-200 rounded-lg p-4 bg-white overflow-hidden">
                                                 <div style={{ fontFamily: 'Arial, Helvetica, sans-serif' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(signaturePreview) }} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {step === 'instruction' && (
                            <div className="flex flex-col items-center justify-center text-center py-8">
                                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6 text-green-600 animate-scale-in"><ExternalLink className="w-10 h-10" /></div>
                                {appMailUebergabe === null ? (
                                    <>
                                        <h3 className="text-xl font-bold text-slate-800">E-Mail Programm geöffnet</h3>
                                        <p className="text-slate-600 mt-2 mb-6 max-w-sm">Das PDF wurde gespeichert. Bitte fügen Sie es an die E-Mail an.</p>
                                    </>
                                ) : (
                                    // Nur in der App: Text je nach Übergabeweg.
                                    <>
                                        <h3 className="text-xl font-bold text-slate-800">{APP_MAIL_TEXTE[appMailUebergabe].titel}</h3>
                                        <p className="text-slate-600 mt-2 mb-6 max-w-sm">{APP_MAIL_TEXTE[appMailUebergabe].text}</p>
                                    </>
                                )}
                                <Button variant="primary" onClick={handleFinish} className="w-full">Schließen</Button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {step === 'config' && !currentStep && (serverSendStatus === 'idle' || serverSendStatus === 'error') && (
                <div className="p-5 bg-slate-50 border-t border-slate-200 flex justify-end gap-3 shrink-0">
                    <Button variant="ghost" onClick={onClose}>{istUebung ? 'Schließen' : 'Abbrechen'}</Button>
                    {istUebung ? (
                        <div className="flex-1 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                            <p className="text-[11px] text-amber-800 leading-relaxed">
                                <b>{UEBUNG_BLOCK.warning}.</b> In einem Übungsprojekt bleibt der Versand
                                gesperrt — Empfänger, Betreff und Text kannst du trotzdem zusammenstellen
                                und über die <b>PDF-Vorschau</b> herunterladen.
                            </p>
                        </div>
                    ) : (
                    /* Nach einem Fehlversuch bleibt der Button gesperrt, bis der Nutzer den Sendeverlauf geprüft und bestätigt hat. */
                    <Button variant="primary" onClick={handleProcess} disabled={isGenerating || (serverSendStatus === 'error' && !retryConfirmed) || (deliveryConfig?.method !== 'poolarserver' && selectedEmails.size === 0)} className={`pl-4 pr-5 ${sendMethod === 'server' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-blue-600 hover:bg-blue-700'}`} data-tutorial="versand.senden">
                        {isGenerating ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : (serverSendStatus === 'error' ? <><RefreshCw className="w-4 h-4 mr-2" /> Trotzdem erneut senden</> : (deliveryConfig?.method === 'poolarserver' ? <><ArrowRight className="w-4 h-4 mr-2" /> Abschließen & Poolarserver öffnen</> : (sendMethod === 'server' ? <><CloudLightning className="w-4 h-4 mr-2" /> Jetzt senden</> : <><ArrowRight className="w-4 h-4 mr-2" /> Speichern & Öffnen</>)))}
                    </Button>
                    )}
                </div>
            )}
        </div>
    </div>
  );
};